import type { ReactNode, Ref } from 'react';
import { ChevronRight, ClipboardList, Play } from 'lucide-react';
import { AccordionItem, AccordionTrigger, Badge, Button, TypePill, cn } from '@wundo/ui';
import type { RoleplayCard } from '@wundo/shared/roleplay.ts';
import { PracticeCardPanel } from './PracticeCardPanel.tsx';
import { formatPracticeHistory, formatRoleplayMeta } from './practiceFormat.ts';
import { m } from '@/paraglide/messages.js';

export interface PracticeCardProps {
  card: RoleplayCard;
  /** Whether a session is being started — from this card or any other. */
  starting: boolean;
  /**
   * Whether this card is the open one. What is inside the panel stays in the
   * DOM either way, so the card cannot read this off its own markup — and the
   * last attempt is only worth fetching for a card somebody is looking at.
   */
  expanded?: boolean;
  /**
   * The way in to changing this one, for the admins who may. Nothing when the
   * caller cannot author — the row is then the same card everybody else gets.
   */
  edit?: ReactNode;
  /**
   * The grip a card is picked up by, before the title. Nothing on a card
   * nobody arranges — a personal roleplay is one person's own list, and the
   * library is only arranged by its authors.
   */
  handle?: ReactNode;
  /* Where the card is while an admin drags it; see `AccordionItem`. */
  ref?: Ref<HTMLDivElement>;
  dragTransform?: string;
  dragTransition?: string;
  dragging?: boolean;
  /**
   * Whether the reader authors the library. It decides one thing on the card:
   * whether the track or cohort it was written for is named. A member is never
   * handed a card of another one, so the pill would only ever repeat their own.
   */
  canManage?: boolean;
  onStart: () => void;
  onOpenFeedback: () => void;
}

/**
 * One practice conversation on the Oefenen page. The row's action follows from
 * the history: practised offers the feedback, not yet offers the practice. The
 * type pill is neutral: the label is free text an admin wrote, and a colour from
 * the gesprekstype palette would be a guess that reads as a fact.
 */
export function PracticeCard({
  card,
  starting,
  expanded = false,
  edit,
  handle,
  ref,
  dragTransform,
  dragTransition,
  dragging,
  canManage = false,
  onStart,
  onOpenFeedback,
}: PracticeCardProps) {
  const practised = card.attempt_count > 0;

  return (
    /*
     * Opening outlines the card rather than tinting the row: the panel below is
     * part of the same card, and a header that changes colour cuts the card in
     * two exactly when it has become one taller thing.
     */
    <AccordionItem
      ref={ref}
      value={card.id}
      dragTransform={dragTransform}
      dragTransition={dragTransition}
      dragging={dragging}
      className='group transition-shadow hover:shadow-hover data-[open]:border-ink'
    >
      {/*
        The action sits beside the trigger rather than inside it: a button
        within a button is not something a browser or a screen reader can make
        sense of, whatever it looks like.

        Beside on a desktop, under it on a phone. Side by side there is not
        enough width for both: the action keeps its size and the title is the
        thing that gives, which left every card reading "TEST …".
      */}
      <div className='flex items-center gap-4 pr-5.5 max-mobile:flex-col max-mobile:items-stretch max-mobile:gap-0 max-mobile:pr-0 max-mobile:pb-4'>
        {/*
          The grip shares the trigger's line on a phone too: below it, with the
          actions, it would no longer say which card it picks up. It stays
          outside the trigger, so a press on it never opens the card.
        */}
        <div className='flex min-w-0 flex-1 items-center'>
          {/* Its icon lines up with where a title starts on a card without one. */}
          {handle !== undefined && <div className='shrink-0 pl-3'>{handle}</div>}
          <div className='min-w-0 flex-1'>
            {/*
              The chevron is drawn by the row, after the action, so the mark that
              says "this opens" ends the row instead of sitting in the middle of
              it.
            */}
            {/*
              No right padding beside the action: the trigger no longer ends the
              row, so its own edge inset is a second one on top of the gap and the
              card's, and those 22px are what pushed the meta line onto two. On a
              phone the trigger does end the row, and there it keeps them.
            */}
            <AccordionTrigger
              chevron={false}
              className={cn(
                'py-5.5 hover:bg-transparent mobile:pr-0',
                handle !== undefined && 'pl-2',
              )}
            >
              <span className='flex min-w-0 flex-1 flex-col gap-1.25'>
                <span className='flex flex-wrap items-center gap-2'>
                  <span className='min-w-0 break-words mobile:truncate'>
                    {card.title}
                  </span>
                  {card.type_label !== null && (
                    <TypePill type='none'>{card.type_label}</TypePill>
                  )}
                  {/* Who it was written for, for the admin who decided that. */}
                  {canManage && card.track !== null && (
                    <Badge tone='info'>
                      {m.practice_card_audience_track({ title: card.track.title })}
                    </Badge>
                  )}
                  {canManage && card.cohort !== null && (
                    <Badge tone='info'>
                      {m.practice_card_audience_cohort({ title: card.cohort.title })}
                    </Badge>
                  )}
                  {/* Only an admin is ever handed a draft, so the badge needs no further guard. */}
                  {card.status === 'draft' && <Badge>{m.practice_draft()}</Badge>}
                </span>
                {/*
                  One line on a desktop, two on a phone — and the separator goes
                  with the second line rather than staying behind on the first,
                  where it would trail the row as a dot belonging to nothing.
                */}
                <span className='flex items-center gap-2.25 text-base font-normal text-ink-secondary max-mobile:flex-col max-mobile:items-start max-mobile:gap-0.5'>
                  <span>{formatRoleplayMeta(card)}</span>
                  <span
                    aria-hidden='true'
                    className='size-0.75 shrink-0 rounded-full bg-ink-faint max-mobile:hidden'
                  />
                  <span>{formatPracticeHistory(card)}</span>
                </span>
              </span>
            </AccordionTrigger>
          </div>
        </div>

        {/*
          Beside the row's own action, not inside the panel: an admin scanning
          the list for the draft they were writing should not have to open it.
        */}
        <div className='flex shrink-0 items-center gap-2 max-mobile:px-5.5'>
          {edit}

          {practised ? (
            <Button
              variant='quiet'
              shape='block'
              className='px-4 font-medium'
              onClick={onOpenFeedback}
            >
              <ClipboardList className='size-3.75' aria-hidden='true' />
              {m.practice_view_feedback()}
            </Button>
          ) : (
            <Button
              variant='quiet'
              shape='block'
              className='px-4 font-medium'
              disabled={starting}
              onClick={onStart}
            >
              <Play className='size-3.5' aria-hidden='true' />
              {m.practice_start()}
            </Button>
          )}

          {/* At the card's edge on a phone, where the stacked action leaves it stranded mid-row. */}
          <ChevronRight
            aria-hidden='true'
            className='size-4.5 shrink-0 text-ink-secondary transition-transform duration-200 group-data-[open]:rotate-90 max-mobile:ml-auto'
          />
        </div>
      </div>

      <PracticeCardPanel
        card={card}
        starting={starting}
        expanded={expanded}
        onStart={onStart}
        onOpenFeedback={onOpenFeedback}
      />
    </AccordionItem>
  );
}
